<template>
  <button
    v-if="metrics.length"
    :id="`assign-accordion-header-${uid}`"
    :aria-expanded="isOpen"
    :aria-controls="`assign-accordion-panel-${uid}`"
    type="button"
    class="w-full rounded-md border border-slate-300 bg-white px-5 py-2.5 shadow-sm outline-none focus:border-transparent focus:ring-1 focus:ring-main-primary focus:ring-offset-2"
    :class="{ 'border-main-primary': isOpen }"
    @click="isOpen = !isOpen"
  >
    <section class="sticky z-10 flex items-center justify-between top-2">
      <div class="flex flex-col items-start gap-y-1.5">
        <h2 class="flex items-center justify-center gap-3 font-medium text-md text-primary">
          <span>{{ topic }}</span>
          <ve-progress
            v-if="savedMetrics.length"
            :progress="Number(progressPercentage.toFixed(0))"
            color="#30BE2E"
            :size="20"
            :hide-legend="true"
            :thickness="4"
          />
          <span
            v-else
            class="h-[20px] w-[20px] rounded-full border-2 border-gray-200"
          />
        </h2>
        <span class="text-xs text-gray-400">{{ metrics.length }} Metrics</span>
      </div>
      <button
        type="button"
        class="rounded-full bg-gray-100 p-1.5 outline-none"
      >
        <ChevronDownSmall
          class="transition-all"
          :class="{ 'rotate-180': isOpen }"
        />
      </button>
    </section>
    <Transition
      enter-class="opacity-0"
      enter-active-class="transition-all duration-300"
      enter-to-class="opacity-100"
      leave-class="opacity-100"
      leave-active-class="transition-all duration-200"
      leave-to-class="opacity-0"
    >
      <section
        v-if="isOpen"
        :id="`assign-accordion-panel-${uid}`"
        :aria-labelledby="`assign-accordion-header-${topic}`"
        class="flex flex-col gap-4 mt-4"
      >
        <div
          v-for="{ id, name, metadata, assets, assignedUsers } in topicMetrics"
          :key="id"
          class="flex items-start justify-between px-4 py-4 text-left border rounded-md assign-metric gap-x-6"
          :class="{ 'border-[#218579]': selectedMetrics.includes(id) }"
          @click.stop="toggleMetricSelection(id)"
        >
          <div class="flex flex-col items-start w-full gap-1">
            <h3
              class="text-sm line-clamp-1 text-primary"
              :title="name"
            >
              {{ name }}
            </h3>
            <span class="text-xs text-gray-400">{{ metadata || "" }}</span>
          </div>
          <div class="flex items-center gap-x-2">
            <!-- Assets -->
            <div class="group relative flex flex-nowrap items-center gap-x-1 text-xs text-[#68737D]">
              <div class="flex items-center gap-1 text-md">
                <BuildingIcon custom-style="size-4" />
                <span>{{ assets.length }}</span>
              </div>
              <div
                v-if="assets.length"
                class="absolute left-0 top-full z-50 hidden rounded-lg border bg-white text-[#344054] shadow group-hover:block"
              >
                <div class="max-h-[250px] overflow-y-auto rounded-lg text-left">
                  <div class="p-3 text-white bg-main-primary">
                    Assets
                  </div>
                  <span
                    v-for="asset in assets"
                    :key="asset.id"
                    class="block py-2 pl-3 pr-6 whitespace-nowrap no-localize-translate"
                  >{{
                    asset.asset_name }}</span>
                </div>
              </div>
            </div>
            <!-- Users -->
            <div class="group relative flex flex-nowrap items-center gap-x-1 text-xs text-[#68737D]">
              <div class="flex items-center gap-1 text-md">
                <UserCircleIcon custom-style="size-4" />
                <span>{{ assignedUsers.length }}</span>
              </div>
              <div
                v-if="assignedUsers.length"
                class="absolute left-0 top-full z-50 hidden rounded-lg border bg-white text-[#344054] shadow group-hover:block"
              >
                <div class="max-h-[250px] overflow-y-auto rounded-lg text-left">
                  <div class="p-3 text-white bg-main-primary">
                    Users
                  </div>
                  <div class="flex flex-col">
                    <div
                      v-for="user in assignedUsers"
                      :key="user.id"
                      class="flex items-center justify-between px-3 py-2 gap-x-6 whitespace-nowrap"
                    >
                      <span class="text-medium no-localize-translate">{{ user.name }}</span>
                      <span class="text-xs">{{ user.role }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Transition>
  </button>
</template>

<script>
import { assetsInjectionKey, usersInjectionKey } from '@/utils/keys'

export default {
  name: 'EsgTopicMetricsAssignAccordion',

  inject: { assetsInjectionKey, usersInjectionKey },

  props: {
    topic: {
      type: String,
      required: true,
    },
    metrics: {
      type: Array,
      required: true,
    },
    savedMetrics: {
      type: Array,
      required: true,
    },
    companyMembers: {
      type: Array,
      default() {
        return []
      },
    },
    allAssets: {
      type: Array,
      default() {
        return []
      },
    },
  },

  emits: ['panelOpened', 'metricDeselected', 'metricsSelected'],

  data() {
    return {
      uid: null,
      isOpen: false,
      topicMetrics: this.transformMetrics(this.metrics),
      selectedMetrics: [],
    }
  },

  computed: {
    progressPercentage() {
      return (this.savedMetrics.length / this.metrics.length) * 100
    },
  },

  watch: {
    metrics(newMetrics) {
      this.topicMetrics = this.transformMetrics(newMetrics)
    },
    isOpen(open) {
      if (open) {
        this.$emit('panelOpened')
      }
    },
  },

  mounted() {
    this.uid = this.$.uid
  },

  methods: {
    transformMetrics(metrics) {
      const listOfAssets = this.allAssets.length ? this.allAssets : this.assetsInjectionKey
      return [...metrics].map((metric) => {
        const { approvers, data_owners } = metric
        const users = [];

        (this.companyMembers.length
          ? this.companyMembers
          : this.usersInjectionKey
        ).forEach(({ name, id }) => {
          const isApprover = approvers.includes(id)
          const isDataOwner = data_owners.includes(id)

          if (isApprover && isDataOwner) {
            users.push({ name, id, role: 'Data Owner and Approver' })
            return
          }
          if (isApprover) {
            users.push({ name, id, role: 'Approver' })
            return
          }
          if (isDataOwner) {
            users.push({ name, id, role: 'Data Owner' })
          }
        })

        metric.assignedUsers = users
        metric.assets = metric.assets.map((asset) => {
          asset = typeof asset === 'object' ? asset : listOfAssets.find(({ id }) => id === Number(asset))
          return asset
        })
        return metric
      })
    },
    toggleMetricSelection(id) {
      if (this.selectedMetrics.includes(id)) {
        const deleteIndex = this.selectedMetrics.findIndex(
          item => item === id,
        )
        this.selectedMetrics.splice(deleteIndex, 1)
        this.$emit('metricDeselected', id)
      }
      else {
        this.selectedMetrics.push(id)
        this.$emit('metricsSelected', this.selectedMetrics)
      }
    },
    deselectMetrics() {
      this.selectedMetrics = []
    },
    hidePanel() {
      this.isOpen = false
    },
  },
}
</script>
